// WP-3.2 (M2 QT-1..6, S-21 `/quotes`): kanban by state on desktop, the same columns stack
// into a status list at narrow widths (`flex-col sm:flex-row` — no separate mobile layout
// needed). Card: party, title, amount or range verbatim (QT-3), age. State changes happen on
// the detail page (S-22) only, per the spec's own "state buttons follow QT-1 transitions only"
// — this board is a read + create surface.
import { formatMoney, type CurrencyCode, type QuotationState } from '@pfos/engine';
import type { QuotationPricing, QuotationResponse } from '@pfos/shared';
import { FileText, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { AmountField, Field, inputClass, selectClass } from '../components/FormKit';
import { FormSheet } from '../components/FormSheet';
import { PartyPicker } from '../components/PartyPicker';
import { ApiError } from '../lib/api';
import { useCreateQuotation, useQuotations } from '../lib/quotations';
import { useParties } from '../lib/parties';

const COLUMNS: { state: QuotationState; label: string }[] = [
  { state: 'draft', label: 'Draft' },
  { state: 'sent', label: 'Sent' },
  { state: 'accepted', label: 'Accepted' },
  { state: 'rejected', label: 'Rejected' },
  { state: 'expired', label: 'Expired' },
  { state: 'converted', label: 'Converted' },
];

function pricingLabel(pricing: QuotationPricing, currency: CurrencyCode): string {
  if (pricing.kind === 'fixed') return formatMoney({ currency, minorUnits: pricing.amountMinor });
  const min = formatMoney({ currency, minorUnits: pricing.minMinor });
  const max = formatMoney({ currency, minorUnits: pricing.maxMinor });
  return `${min}–${max}`;
}

function ageLabel(createdAt: string): string {
  const days = Math.floor((Date.now() - Date.parse(createdAt)) / 86_400_000);
  if (days <= 0) return 'today';
  return days === 1 ? '1 day' : `${String(days)} days`;
}

function QuotationCard({ quotation }: { quotation: QuotationResponse }): React.JSX.Element {
  return (
    <Link
      to={`/quotes/${quotation.id}`}
      className="block rounded-[var(--radius-card)] border border-border bg-surface p-3 hover:border-primary"
    >
      <p className="text-sm font-medium text-ink">{quotation.party}</p>
      <p className="text-xs text-muted">{quotation.title}</p>
      <p className="mt-1 text-sm text-ink">{pricingLabel(quotation.pricing, quotation.currency)}</p>
      <p className="mt-1 text-xs text-muted">{ageLabel(quotation.createdAt)} old</p>
    </Link>
  );
}

type PricingKind = 'fixed' | 'range';

function NewQuotationForm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [party, setParty] = useState('');
  const [partyId, setPartyId] = useState('');
  const [title, setTitle] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [pricingKind, setPricingKind] = useState<PricingKind>('fixed');
  const [amount, setAmount] = useState('');
  const [rangeMin, setRangeMin] = useState('');
  const [rangeMax, setRangeMax] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateQuotation();
  const { data: parties } = useParties(false);

  useEffect(() => {
    if (open) {
      setParty('');
      setPartyId('');
      setTitle('');
      setCurrency('PKR');
      setPricingKind('fixed');
      setAmount('');
      setRangeMin('');
      setRangeMax('');
      setExpiryDate('');
      setError(null);
    }
  }, [open]);

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="New quotation"
      submitLabel="Save"
      submitting={createMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (party.trim() === '') {
          setError('Party is required');
          return;
        }
        if (title.trim() === '') {
          setError('Title is required');
          return;
        }
        let pricing: QuotationPricing;
        if (pricingKind === 'fixed') {
          const amountMinor = Math.round(Number(amount) * 100);
          if (!(amountMinor > 0)) {
            setError('Amount must be a positive number');
            return;
          }
          pricing = { kind: 'fixed', amountMinor };
        } else {
          const minMinor = Math.round(Number(rangeMin) * 100);
          const maxMinor = Math.round(Number(rangeMax) * 100);
          if (!(minMinor > 0) || !(maxMinor > 0) || minMinor >= maxMinor) {
            setError('Range minimum must be positive and less than the maximum');
            return;
          }
          pricing = { kind: 'range', minMinor, maxMinor };
        }
        createMutation.mutate(
          {
            party: party.trim(),
            ...(partyId !== '' ? { partyId } : {}),
            title: title.trim(),
            pricing,
            currency,
            ...(expiryDate !== '' ? { expiryDate: new Date(expiryDate).toISOString() } : {}),
          },
          {
            onSuccess: () => {
              onOpenChange(false);
            },
            onError: (e) => {
              setError(e instanceof ApiError ? e.message : 'Could not save');
            },
          },
        );
      }}
    >
      {error !== null && <p className="mb-3 text-sm text-danger">{error}</p>}
      <Field label="Party (optional link)" htmlFor="quote-party-id">
        <PartyPicker
          id="quote-party-id"
          value={partyId}
          onChange={(id) => {
            setPartyId(id);
            const matched = (parties ?? []).find((p) => p.id === id);
            if (matched !== undefined) setParty(matched.name);
          }}
        />
      </Field>
      <Field label="Party name" htmlFor="quote-party">
        <input
          id="quote-party"
          className={inputClass}
          value={party}
          onChange={(e) => {
            setParty(e.target.value);
          }}
        />
      </Field>
      <Field label="Title" htmlFor="quote-title">
        <input
          id="quote-title"
          className={inputClass}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
      </Field>
      <Field label="Currency" htmlFor="quote-currency">
        <select
          id="quote-currency"
          className={selectClass}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value as CurrencyCode);
          }}
        >
          {['PKR', 'USD', 'EUR', 'GBP'].map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Pricing" htmlFor="quote-pricing-kind">
        <select
          id="quote-pricing-kind"
          className={selectClass}
          value={pricingKind}
          onChange={(e) => {
            setPricingKind(e.target.value as PricingKind);
          }}
        >
          <option value="fixed">Fixed amount</option>
          <option value="range">Range (min–max)</option>
        </select>
      </Field>
      {pricingKind === 'fixed' ? (
        <Field label="Amount" htmlFor="quote-amount">
          <AmountField id="quote-amount" currency={currency} value={amount} onChange={setAmount} />
        </Field>
      ) : (
        <div className="flex gap-3">
          <Field label="Min" htmlFor="quote-range-min">
            <AmountField
              id="quote-range-min"
              currency={currency}
              value={rangeMin}
              onChange={setRangeMin}
            />
          </Field>
          <Field label="Max" htmlFor="quote-range-max">
            <AmountField
              id="quote-range-max"
              currency={currency}
              value={rangeMax}
              onChange={setRangeMax}
            />
          </Field>
        </div>
      )}
      <Field label="Expiry (optional)" htmlFor="quote-expiry">
        <input
          id="quote-expiry"
          type="date"
          className={inputClass}
          value={expiryDate}
          onChange={(e) => {
            setExpiryDate(e.target.value);
          }}
        />
      </Field>
    </FormSheet>
  );
}

export function QuotationsPage(): React.JSX.Element {
  const { data: quotations, isLoading } = useQuotations();
  const [formOpen, setFormOpen] = useState(false);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Quotations</h1>
        <button
          type="button"
          className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
          onClick={() => {
            setFormOpen(true);
          }}
        >
          <Plus size={16} /> New quotation
        </button>
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && (quotations === undefined || quotations.length === 0) && (
        <EmptyState
          icon={FileText}
          message="No quotations yet."
          action={{
            label: 'New quotation',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {quotations !== undefined && quotations.length > 0 && (
        <div className="flex flex-col gap-4 sm:flex-row sm:overflow-x-auto">
          {COLUMNS.map((col) => {
            const inColumn = quotations.filter((q) => q.status === col.state);
            return (
              <div key={col.state} className="sm:w-64 sm:shrink-0">
                <p className="mb-2 text-xs font-semibold uppercase text-muted">
                  {col.label} ({inColumn.length})
                </p>
                <div className="space-y-2">
                  {inColumn.map((q) => (
                    <QuotationCard key={q.id} quotation={q} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NewQuotationForm open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
